// Shared types for Ultrasonic Testing (UT) and Radiographic Testing (RT)

export type WeldNdtMethod = 'UT' | 'RT'
export type WeldNdtStatus = 'Accepted' | 'Not Accepted'
export type WeldNdtMaterialType = 'Carbon steel' | 'Stainless steel' | 'Aluminium'

export const WELD_NDT_TEST_SITES = ['Bergen', 'Mongstad', 'Ågotnes'] as const
export type WeldNdtTestSite = (typeof WELD_NDT_TEST_SITES)[number]
export const WELD_NDT_DEFAULT_TEST_SITE: WeldNdtTestSite = 'Bergen'

export const WELD_NDT_MATERIALS: Array<{ value: WeldNdtMaterialType; label: string }> = [
  { value: 'Carbon steel', label: 'Karbonstål' },
  { value: 'Stainless steel', label: 'Rustfritt stål' },
  { value: 'Aluminium', label: 'Aluminium' },
]

export interface WeldNdtReportData {
  testDate: string
  testSite?: string
  reportNumber: string
  objects: string[]
  objectDescription: string
  issuedBy: string
  verifiedBy?: string
  status: WeldNdtStatus
  material: WeldNdtMaterialType | string
  drawing?: string
  diameter?: string
  thickness?: string
  welder?: string
  weldingProcedure?: string
  joint?: string
  pwht?: string
  fillerMet?: string
  weldingMethod?: string
  surfaceCondition?: string
  temperatureOfObject?: string
  testProcedure?: string
  testAcceptance?: string
  extentOfExamination: string
  referenceStandards?: string
  indications?: string
  comments?: string
  certificationNumber: string
  sequenceNumber: string
  company?: string
  report_code?: string | null
  attachedImage?: string
}

export interface WeldNdtFormData {
  testDate: string
  testSite: string
  reportNumber: string
  objects: string[]
  objectDescription: string
  issuedBy: string
  verifiedBy?: string
  status: WeldNdtStatus
  material: WeldNdtMaterialType
  drawing: string
  diameter: string
  thickness: string
  welder: string
  weldingProcedure: string
  joint: string
  pwht: string
  fillerMet: string
  weldingMethod: string
  surfaceCondition: string
  temperatureOfObject: string
  testProcedure: string
  testAcceptance: string
  extentOfExamination: string
  referenceStandards: string
  indications: string
  comments?: string
  company?: string
  report_code?: string | null
  attachedImage?: string
}

export interface WeldNdtMethodConfig {
  method: WeldNdtMethod
  methodTitle: string
  highlight: string
  formTitle: string
  previewTitle: string
  submitLabel: string
  certSuffix: string
  defaultProcedure: string
  defaultAcceptance: string
  defaultReference: string
  procedureOptions: readonly string[]
}

export const UT_METHOD_CONFIG: WeldNdtMethodConfig = {
  method: 'UT',
  methodTitle: 'Ultrasonic',
  highlight: 'Ultrasonic',
  formTitle: 'Testing',
  previewTitle: 'UT-rapport forhåndsvisning',
  submitLabel: 'Generer UT-rapport',
  certSuffix: '-N2-U',
  defaultProcedure: 'UT DNV-CG-0051 in accordance with ISO 17640',
  defaultAcceptance: 'ISO 11666 Level 2',
  defaultReference: 'DNV-CG-0051',
  procedureOptions: [
    'UT DNV-CG-0051 in accordance with ISO 17640',
    'ISO 17640',
    'ASME Section V Article 5',
    'EN ISO 17640',
  ],
}

export const RT_METHOD_CONFIG: WeldNdtMethodConfig = {
  method: 'RT',
  methodTitle: 'Radiographic',
  highlight: 'Radiographic',
  formTitle: 'Testing',
  previewTitle: 'RT-rapport forhåndsvisning',
  submitLabel: 'Generer RT-rapport',
  certSuffix: '-N2-R',
  defaultProcedure: 'RT DNV-CG-0051',
  defaultAcceptance: 'ISO 10675-1 Level 1',
  defaultReference: 'DNV-CG-0051',
  procedureOptions: [
    'RT DNV-CG-0051',
    'RT DNV-CG-0051 in accordance with ISO 17636-1',
    'ISO 17636-1',
    'ISO 17636-2',
    'ASME Section V Article 2',
  ],
}

export function createDefaultWeldNdtFormData(config: WeldNdtMethodConfig): WeldNdtFormData {
  return {
    testDate: new Date().toISOString().split('T')[0],
    testSite: WELD_NDT_DEFAULT_TEST_SITE,
    reportNumber: '',
    objects: [''],
    objectDescription: '',
    issuedBy: '',
    verifiedBy: '',
    status: 'Accepted',
    material: 'Carbon steel',
    drawing: '',
    diameter: '',
    thickness: '',
    welder: '',
    weldingProcedure: '',
    joint: '',
    pwht: '',
    fillerMet: '',
    weldingMethod: '',
    surfaceCondition: 'Cleaned',
    temperatureOfObject: '0-35°C',
    testProcedure: config.defaultProcedure,
    testAcceptance: config.defaultAcceptance,
    extentOfExamination: '100',
    referenceStandards: config.defaultReference,
    indications: '',
    comments: '',
    company: '',
    report_code: null,
    attachedImage: undefined,
  }
}
